import { useEffect, useState } from 'react'
import { useMutation } from '@apollo/client'
import gql from 'graphql-tag'
import { usePath } from 'lib/core/hooks'
import { useCheckout } from 'lib/modules/checkout'
import { PAYMENT_METHOD } from 'lib/core/resources/constants'
import { localStorage } from 'lib/core/services/localStorage'

interface CreatePaypalExpressTokenResponse {
  createPaypalExpressToken: {
    token: string
    paypal_urls: {
      start: string
      edit: string
    }
  }
}

interface SetPaymentMethodOnCartResponse {
  setPaymentMethodOnCart: {
    cart: {
      selected_payment_method: {
        code: string
        title: string
      }
    }
  }
}

export interface PaypalExpressState {
  token: string | null
  payerId: string | null
}

export const setPaymentMethodOnCartMutation = gql`
  mutation setPaymentMethodOnCart(
    $payerId: String!
    $token: String!
    $cartId: String!
  ) {
    setPaymentMethodOnCart(
      input: {
        cart_id: $cartId,
        payment_method: {
          code: "${PAYMENT_METHOD.PAYPAL_EXPRESS}"
          paypal_express: {
            payer_id: $payerId
            token: $token
          }
        }
      }
    ) {
      cart {
        selected_payment_method {
          code
          title
        }
      }
    }
  }
`

export const createPaypalExpressClientTokenMutation = gql`
  mutation createPaypalExpressToken(
    $cartId: String!
    $returnUrl: String!
    $cancelUrl: String!
  ) {
    createPaypalExpressToken(
      input: {
        cart_id: $cartId
        code: "${PAYMENT_METHOD.PAYPAL_EXPRESS}"
        express_button: true
        urls: {
          return_url: $returnUrl,
          cancel_url: $cancelUrl
        }
      }
    ) {
      token
      paypal_urls {
        start
        edit
      }
    }
  }
`

export const usePaypalExpress = () => {
  const { lockCheckout, unlockCheckout, cartId, placeOrder } = useCheckout()
  const path = usePath()
  const [error, setError] = useState(false)
  const [createPaypalExpressToken] = useMutation<CreatePaypalExpressTokenResponse>(createPaypalExpressClientTokenMutation)
  const [setPaymentMethodOnCart] = useMutation<SetPaymentMethodOnCartResponse>(setPaymentMethodOnCartMutation)
  const url = `${path.paypalExpress?.slice(1)}#${encodeURIComponent(window.location.pathname.slice(1))}`

  useEffect(() => {
    const { token, payerId }: PaypalExpressState = localStorage.get('paypalExpressState') ?? {}
    setError(token === null)

    localStorage.remove('paypalExpressState')

    const setPaymentMethod = async () => {
      await setPaymentMethodOnCart({
        variables: {
          payerId,
          token,
          cartId
        }
      })
      unlockCheckout()
      placeOrder()
    }

    if (token && payerId) {
      lockCheckout()
      try {
        setPaymentMethod()
      } catch (e) {
        unlockCheckout()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartId, setPaymentMethodOnCart])

  return {
    authorizeUser: async () => {
      lockCheckout()
      return createPaypalExpressToken({
        variables: {
          cartId,
          returnUrl: url,
          cancelUrl: url
        }
      }).then(response => {
        const paypalExpressUrl = response.data?.createPaypalExpressToken.paypal_urls.start
        if (!paypalExpressUrl) throw new Error('Paypal Error')
        window.location.href = paypalExpressUrl
      })
    },
    error
  }
}
